import { useState, useMemo } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Upload, Search, ShieldAlert, Loader2, MapPin, ArrowRight, CheckCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProofUpload() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const partnerId = user?.partner_id || user?.id;
  const partnerType = user?.partner_type || "NETWORK";

  const [searchInput, setSearchInput] = useState("");
  const [searching, setSearching] = useState(false);
  const [shipment, setShipment] = useState(null);

  const [actualWeight, setActualWeight] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [weightPhotoUrl, setWeightPhotoUrl] = useState("");
  const [midmileLrUrl, setMidmileLrUrl] = useState("");
  const [midmileAmount, setMidmileAmount] = useState("");
  const [extras, setExtras] = useState("15");
  const [submitting, setSubmitting] = useState(false);

  const volumetric = useMemo(() => {
    const l = parseFloat(length) || 0;
    const w = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;
    return (l * w * h) / 4000;
  }, [length, width, height]);

  const chargeable = useMemo(() => {
    const a = parseFloat(actualWeight) || 0;
    return Math.max(a, volumetric, 10);
  }, [actualWeight, volumetric]);

  // Gate REVENUE partners (AFTER all hooks to respect rules-of-hooks)
  if (partnerType === "REVENUE") {
    return (
      <DashboardLayout portalColor="green">
        <div className="flex flex-col items-center justify-center h-full text-center py-16" data-testid="access-denied">
          <ShieldAlert className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-sm text-slate-500 max-w-md">
            REVENUE partners cannot upload proof. This feature is only available to
            NETWORK and EXECUTION partners.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const doSearch = async () => {
    if (!searchInput.trim()) return;
    setSearching(true);
    setShipment(null);
    try {
      const res = await fetch(`${API}/api/shipments?awb=${encodeURIComponent(searchInput.trim())}`,
                              { headers });
      if (res.ok) {
        const d = await res.json();
        const items = d.items || (Array.isArray(d) ? d : []);
        if (items.length > 0) {
          setShipment(items[0]);
        } else {
          toast.error("No shipment found for that AWB / Order ID");
        }
      } else {
        toast.error("Search failed");
      }
    } catch (e) { toast.error("Error: " + e.message); }
    setSearching(false);
  };

  const canSubmit = () => {
    if (!shipment) return false;
    if (!actualWeight || !length || !width || !height) return false;
    if (partnerType === "NETWORK") {
      if (!midmileLrUrl.trim() || !midmileAmount) return false;
    }
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) return toast.error("Fill all required fields");
    setSubmitting(true);
    try {
      const body = {
        actual_weight_kg: parseFloat(actualWeight),
        length_cm: parseFloat(length),
        width_cm: parseFloat(width),
        height_cm: parseFloat(height),
        weight_photo_url: weightPhotoUrl || null,
      };
      if (partnerType === "NETWORK") {
        body.midmile_lr_url = midmileLrUrl;
        body.actual_amount = parseFloat(midmileAmount);
        body.extras_per_shipment = parseFloat(extras) || 15;
      }
      const res = await fetch(
        `${API}/api/v2/partners/${partnerId}/shipments/${shipment.id}/midmile`,
        { method: "POST", headers, body: JSON.stringify(body) }
      );
      if (res.ok) {
        toast.success(`Proof submitted. Chargeable weight: ${chargeable.toFixed(2)}kg`);
        // Reset
        setShipment(null);
        setSearchInput("");
        setActualWeight(""); setLength(""); setWidth(""); setHeight("");
        setWeightPhotoUrl(""); setMidmileLrUrl(""); setMidmileAmount(""); setExtras("15");
      } else {
        const err = await res.json().catch(() => ({ detail: "Submission failed" }));
        toast.error(err.detail || "Submission failed");
      }
    } catch (e) { toast.error("Error: " + e.message); }
    setSubmitting(false);
  };

  return (
    <DashboardLayout portalColor="green">
      <div className="space-y-5 max-w-4xl" data-testid="proof-upload">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Upload Proof</h1>
          <p className="text-sm text-slate-500">
            {partnerType === "NETWORK"
              ? "Submit weight photo, midmile LR and actual amount"
              : "Submit weight photo for shipment reconciliation"}
          </p>
        </div>

        {/* SEARCH */}
        <div className="bg-white border rounded-lg p-4">
          <label className="block text-xs font-semibold text-slate-600 uppercase mb-2">
            Find Shipment
          </label>
          <div className="flex gap-2">
            <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                   onKeyDown={(e) => e.key === "Enter" && doSearch()}
                   placeholder="Enter AWB or Order ID" className="flex-1"
                   data-testid="search-input" />
            <Button onClick={doSearch} disabled={searching} data-testid="search-btn">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              <span className="ml-1.5">Search</span>
            </Button>
          </div>
        </div>

        {/* SHIPMENT CARD */}
        {shipment && (
          <div className="bg-blue-50/60 border-blue-200 border rounded-lg p-4" data-testid="shipment-card">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">AWB</div>
                <div className="font-mono">{shipment.awb || shipment.tracking_id || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Customer</div>
                <div>{shipment.customer_name || shipment.origin?.name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Route</div>
                <div className="flex items-center gap-1 text-xs">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  {shipment.origin?.city || shipment.origin?.pincode || "—"}
                  <ArrowRight className="w-3 h-3" />
                  {shipment.destination?.city || shipment.destination?.pincode || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500 font-semibold">Booked Weight</div>
                <div>{shipment.chargeable_weight || shipment.package?.weight || 0} kg</div>
              </div>
              <div className="col-span-2 md:col-span-4">
                <Badge className="text-[10px] bg-slate-100 text-slate-700">
                  Status: {(shipment.status || "BOOKED").toUpperCase()}
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* UPLOAD FORM */}
        {shipment && (
          <div className="bg-white border rounded-lg p-5 space-y-4" data-testid="upload-form">
            <h3 className="text-sm font-bold text-slate-700 uppercase">Weight &amp; Dimensions</h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Actual Weight (kg)" required>
                <Input type="number" value={actualWeight}
                       onChange={(e) => setActualWeight(e.target.value)}
                       data-testid="input-actual-weight" />
              </Field>
              <Field label="Length (cm)" required>
                <Input type="number" value={length} onChange={(e) => setLength(e.target.value)}
                       data-testid="input-length" />
              </Field>
              <Field label="Width (cm)" required>
                <Input type="number" value={width} onChange={(e) => setWidth(e.target.value)}
                       data-testid="input-width" />
              </Field>
              <Field label="Height (cm)" required>
                <Input type="number" value={height} onChange={(e) => setHeight(e.target.value)}
                       data-testid="input-height" />
              </Field>
            </div>

            {/* Auto weights */}
            <div className="bg-slate-50 border rounded p-3 text-xs space-y-1" data-testid="weight-calc">
              <div>
                Volumetric Weight: <span className="font-bold">{volumetric.toFixed(2)} kg</span>
                <span className="text-slate-400"> (divisor: 4000)</span>
              </div>
              <div>
                Chargeable Weight: <span className="font-bold text-emerald-600">{chargeable.toFixed(2)} kg</span>
                <span className="text-slate-400"> (max of actual vs volumetric vs 10)</span>
              </div>
            </div>

            <Field label="Weight Photo URL" hint="Upload to S3 and paste link (Phase 8: native upload)">
              <Input value={weightPhotoUrl} onChange={(e) => setWeightPhotoUrl(e.target.value)}
                     placeholder="https://..." data-testid="input-weight-photo" />
            </Field>

            {/* MIDMILE — only for NETWORK */}
            {partnerType === "NETWORK" && (
              <>
                <div className="border-t pt-4 mt-2">
                  <h3 className="text-sm font-bold text-slate-700 uppercase mb-3">Midmile Proof</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Midmile LR Photo URL" required>
                      <Input value={midmileLrUrl} onChange={(e) => setMidmileLrUrl(e.target.value)}
                             placeholder="https://..." data-testid="input-lr-url" />
                    </Field>
                    <Field label="Midmile Actual Amount (₹)" required>
                      <Input type="number" value={midmileAmount}
                             onChange={(e) => setMidmileAmount(e.target.value)}
                             data-testid="input-midmile-amount" />
                    </Field>
                    <Field label="Extras per Shipment (₹)">
                      <Input type="number" value={extras} onChange={(e) => setExtras(e.target.value)}
                             data-testid="input-extras" />
                    </Field>
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={submit} disabled={!canSubmit() || submitting}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="submit-proof-btn">
                {submitting
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <Upload className="w-4 h-4 mr-1.5" />}
                Submit Proof
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}
